/**
 * Apps platforma (F1) — store, instalace, nav feed + platformní katalog.
 *
 * Zrcadlí `app/routers/admin/apps_store.py` a `platform_apps.py`.
 * Appky jsou 3. gating dimenze mimo page rules / entitlementy — nav
 * položky nainstalovaných appek skládá AdminLayout z `useInstalledApps`.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

import { adminKeys } from "../keys";

const API = "/api/v1";

/**
 * Kam do admin menu stránka appky patří (KAN-1358).
 *
 * `group` je id skupiny `ADMIN_NAV_GROUPS`, `subgroup` id podskupiny
 * uvnitř ní. Validuje to manifest na BE proti jedinému seznamu
 * (`app/services/admin_nav_placement.py`), takže sem už chodí jen
 * existující id. `null` = manifest placement nedeklaruje a položka jde
 * za kotvu „Aplikace" jako dřív.
 */
export interface AppPagePlacement {
  group: string;
  subgroup: string | null;
}

export interface AppStorePage {
  id: string;
  nav_label: Record<string, string>;
  /** Věta do popisného sloupce menu; prázdná mapa = manifest ji nenese. */
  nav_desc: Record<string, string>;
  placement: AppPagePlacement | null;
}

export interface AppScopeDetail {
  key: string;
  description_i18n: Record<string, string>;
}

export interface AppStoreItem {
  slug: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  icon_url: string | null;
  author_name: string;
  price_monthly_cents: number | null;
  currency: string;
  /** Nejnovější publikovaná verze — to, co dostaneš při instalaci. */
  version: string;
  scopes: string[];
  scope_details: AppScopeDetail[];
  pages: AppStorePage[];
  installed: boolean;
  install_status: string | null;
  /** Verze, kterou tenant SKUTEČNĚ běží (`null` = nenainstalováno). */
  installed_version: string | null;
  /** Je na co upgradovat? Rozhoduje BE, ne semver porovnání ve FE. */
  upgrade_available: boolean;
  /** Co tenant odsouhlasil dnes — rozhoduje, jestli je potřeba re-consent. */
  granted_scopes: string[];
}

export interface InstalledApp {
  slug: string;
  name_i18n: Record<string, string>;
  icon_url: string | null;
  status: string;
  version: string;
  pages: AppStorePage[];
  granted_scopes: string[];
}

/**
 * Katalog + stav instalace. Na rozdíl od `useInstalledApps` nese i
 * instalace, které NEBĚŽÍ (`suspended`) — proto se z něj dá zjistit,
 * že appka není mrtvá, jen nezaplacená.
 */
export function useAppStore(enabled: boolean = true) {
  return useQuery<{ items: AppStoreItem[] }>({
    queryKey: ["admin", "apps", "store"],
    queryFn: () => apiFetch(`${API}/admin/apps/store`),
    staleTime: 30_000,
    enabled,
  });
}

export function useInstalledApps(enabled: boolean = true) {
  return useQuery<{ items: InstalledApp[] }>({
    queryKey: ["admin", "apps", "installed"],
    queryFn: () => apiFetch(`${API}/admin/apps/installed`),
    // Nav se z toho skládá při každém mountu shellu — drž déle fresh.
    staleTime: 60_000,
    // Platformní shell nemá aktivní org → dotaz se tam nevolá.
    enabled,
  });
}

export function useInstallApp() {
  const qc = useQueryClient();
  return useMutation<
    { slug: string; status: string },
    Error,
    { slug: string; accepted_scopes: string[] }
  >({
    mutationFn: ({ slug, accepted_scopes }) =>
      apiFetch(`${API}/admin/apps/store/${encodeURIComponent(slug)}/install`, {
        method: "POST",
        body: { accepted_scopes },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "apps"] });
      // KAN-1379: app pages are part of the menu the shell draws.
      qc.invalidateQueries({ queryKey: ["admin", "menu"] });
      // KAN-385: sloučená plocha čte stav z ``/admin/extensions``.
      // Bez téhle invalidace by po instalaci zůstal starý štítek.
      qc.invalidateQueries({ queryKey: adminKeys.extensions() });
    },
  });
}

export interface AppUpgradeResponse {
  slug: string;
  status: string;
  from_version: string;
  to_version: string;
  granted_scopes: string[];
}

/**
 * Přepnutí instalace na nejnovější publikovanou verzi.
 *
 * Předplatné placené appky zůstává — odinstalovat + nainstalovat znovu
 * ho ruší, takže tohle je jediná cesta k nové verzi placené appky.
 *
 * `accepted_scopes` posílej JEN po zobrazení consentu; žádá-li nová
 * verze scopes navíc, BE bez nich upgrade odmítne
 * (`app_upgrade_requires_consent`).
 */
export function useUpgradeApp() {
  const qc = useQueryClient();
  return useMutation<
    AppUpgradeResponse,
    Error,
    { slug: string; accepted_scopes: string[] }
  >({
    mutationFn: ({ slug, accepted_scopes }) =>
      apiFetch(`${API}/admin/apps/store/${encodeURIComponent(slug)}/upgrade`, {
        method: "POST",
        body: { accepted_scopes },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "apps"] });
      // KAN-1379: app pages are part of the menu the shell draws.
      qc.invalidateQueries({ queryKey: ["admin", "menu"] });
      // KAN-385: sloučená plocha čte stav z ``/admin/extensions``.
      // Bez téhle invalidace by po instalaci zůstal starý štítek.
      qc.invalidateQueries({ queryKey: adminKeys.extensions() });
    },
  });
}

/**
 * Config nainstalované appky (KAN-1595) — zrcadlí `AppConfigOut`
 * z `app/routers/admin/apps_store.py`.
 *
 * Co formulář nabídne, říká manifest pinnuté verze: povinné, volitelné
 * a tajné klíče a jejich popisky. Hodnota tajného klíče sem nikdy
 * nepřijde — jen to, jestli je nastavený (`configured_secret_keys`).
 */
export interface AppConfig {
  slug: string;
  config: Record<string, unknown>;
  secret_config_keys: string[];
  configured_secret_keys: string[];
  required_config_keys: string[];
  optional_config_keys: string[];
  config_labels: Record<string, Record<string, string>>;
  /** Appka nabízí „Test připojení" (protokol `ConnectionTester`, KAN-1596). */
  supports_connection_test: boolean;
}

export interface AppConnectionTestResult {
  slug: string;
  ok: boolean;
  checks: { key: string; ok: boolean; detail: string | null }[];
  /** Hodnoty k výběru pro klíč configu, např. `{db_name: [{value, label}]}`. */
  choices: Record<string, { value?: unknown; label?: unknown }[]>;
}

/**
 * „Test připojení" s neuloženými hodnotami formuláře (KAN-1596). Nic
 * neukládá — prázdné heslo znamená uložené, stejně jako při uložení.
 */
export function useTestAppConnection() {
  return useMutation<
    AppConnectionTestResult,
    Error,
    { slug: string; config: Record<string, unknown> }
  >({
    mutationFn: ({ slug, config }) =>
      apiFetch<AppConnectionTestResult>(
        `${API}/admin/apps/store/${encodeURIComponent(slug)}/config/test`,
        { method: "POST", body: { config } },
      ),
  });
}

const appConfigKey = (slug: string) => ["admin", "apps", "config", slug] as const;

export function useAppConfig(slug: string, enabled = true) {
  return useQuery<AppConfig>({
    queryKey: appConfigKey(slug),
    queryFn: () =>
      apiFetch<AppConfig>(
        `${API}/admin/apps/store/${encodeURIComponent(slug)}/config`,
      ),
    enabled,
  });
}

/**
 * PATCH configu. Nezaslaný klíč se nemění; u tajného klíče ani prázdný
 * řetězec — odvolání credentialu je explicitní `null`.
 */
export function useUpdateAppConfig() {
  const qc = useQueryClient();
  return useMutation<
    AppConfig,
    Error,
    { slug: string; config: Record<string, unknown> }
  >({
    mutationFn: ({ slug, config }) =>
      apiFetch<AppConfig>(
        `${API}/admin/apps/store/${encodeURIComponent(slug)}/config`,
        { method: "PATCH", body: { config } },
      ),
    onSuccess: (data, { slug }) => {
      qc.setQueryData(appConfigKey(slug), data);
    },
  });
}

export function useUninstallApp() {
  const qc = useQueryClient();
  return useMutation<{ slug: string; status: string }, Error, string>({
    mutationFn: (slug) =>
      apiFetch(
        `${API}/admin/apps/store/${encodeURIComponent(slug)}/uninstall`,
        { method: "POST", body: {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "apps"] });
      // KAN-1379: app pages are part of the menu the shell draws.
      qc.invalidateQueries({ queryKey: ["admin", "menu"] });
      // KAN-385: sloučená plocha čte stav z ``/admin/extensions``.
      // Bez téhle invalidace by po instalaci zůstal starý štítek.
      qc.invalidateQueries({ queryKey: adminKeys.extensions() });
    },
  });
}

export interface AppSlotWidget {
  app_slug: string;
  page_id: string;
  name_i18n: Record<string, string>;
  /**
   * Filtr render site (KAN-1361): `{node_kind}` nebo `{process_type}`,
   * jen u slotů, které znají úkol. `null` = widget u každého úkolu.
   */
  when: Record<string, string> | null;
  /**
   * Slot nad výběrem operací (KAN-1611): `process_type` operací, které
   * widget umí (z `capabilities` manifestu). Host mu pošle a nabídne jen
   * je; prázdný seznam = nic. `null` = slot nefiltruje.
   */
  process_types?: string[] | null;
  /**
   * Q8 (v66) — `compute:` uzly aplikace, které v mapě tenanta chybí
   * (operace z palety mapy). Host nabídne „Vložit do mapy" dřív, než
   * aplikace narazí na 409 `compute_node_not_placed`.
   */
  missing_operations?: {
    slug: string;
    label_cs: string;
    label_en: string;
    operation_kind: string;
  }[];
}

/**
 * Odkud host bere feed slotu a token appky. `shopfloor` = úzký výřez
 * dílny (KAN-1694): jen slot `shopfloor.task` a jen appky, které v něm
 * widget mají — terminál do `/admin` nesmí (KAN-1662/1695).
 */
export type AppHostSurface = "admin" | "shopfloor";

/** Widgety appek pro jeden slot systémové stránky (F4). */
export function useAppSlotWidgets(slot: string, surface: AppHostSurface = "admin") {
  return useQuery<{ items: AppSlotWidget[] }>({
    queryKey: [surface, "apps", "slots", slot],
    queryFn: () =>
      surface === "shopfloor"
        ? apiFetch(`${API}/shopfloor/apps/widgets`)
        : apiFetch(`${API}/admin/apps/slots/${encodeURIComponent(slot)}`),
    staleTime: 60_000,
  });
}

export interface AppCheckoutResponse {
  session_id: string;
  url: string | null;
}

/** Checkout placené appky — instalace vznikne až z webhooku po platbě. */
export function useStartAppCheckout() {
  return useMutation<AppCheckoutResponse, Error, string>({
    mutationFn: (slug) =>
      apiFetch(`${API}/admin/apps/store/${encodeURIComponent(slug)}/checkout`, {
        method: "POST",
        body: {
          success_url: `${window.location.origin}${window.location.pathname}?app_checkout=success`,
          cancel_url: `${window.location.origin}${window.location.pathname}?app_checkout=cancel`,
        },
      }),
  });
}

export interface AppTokenResponse {
  token: string;
  expires_at: string;
  bundle_url: string;
  scopes: string[];
}

/**
 * Mint krátkodobého app tokenu pro sandboxovaný runtime.
 *
 * Volá HOST (admin se session), ne appka — ta na admin surface nedosáhne.
 * Mutace (ne query), aby se token nikdy nekešoval v query cache.
 */
/**
 * Token pro sandbox appky. `page_id` říká, kterou stránku host spouští —
 * BE podle ní vynucuje `visible_when` (KAN-1359): skrytá stránka token
 * nedostane (403 + `X-Page-Hidden`). Bez `page_id` = první stránka.
 */
export function useMintAppToken(slug: string, surface: AppHostSurface = "admin") {
  const base = surface === "shopfloor" ? `${API}/shopfloor/apps` : `${API}/admin/apps`;
  return useMutation<AppTokenResponse, Error, { page_id?: string }>({
    mutationFn: ({ page_id }) =>
      apiFetch(`${base}/${encodeURIComponent(slug)}/token`, {
        method: "POST",
        body: page_id ? { page_id } : {},
      }),
  });
}

/** Subjekt platformního tisku štítku pro záznam appky (KAN-1609). */
export interface AppLabelSubject {
  entity_kind: string;
  entity_id: number;
}

/**
 * Záznam appky → subjekt štítku. Volá HOST se slugem rámu, ze kterého
 * žádost přišla — záznam cizí appky nebo organizace je 404.
 */
export function fetchAppLabelSubject(
  slug: string,
  table: string,
  key: string,
  surface: AppHostSurface = "admin",
): Promise<AppLabelSubject> {
  // Dílna má vlastní výřez — terminál do /admin nesmí (KAN-1751).
  const base = surface === "shopfloor" ? `${API}/shopfloor/apps` : `${API}/admin/apps`;
  return apiFetch(
    `${base}/${encodeURIComponent(slug)}/entities/${encodeURIComponent(table)}/${encodeURIComponent(key)}/label-subject`,
  );
}

// --- Platformní katalog (RequireFullPlatformAdmin) --------------------

export interface PlatformAppVersion {
  id: number;
  version: string;
  state: string;
  manifest: Record<string, unknown>;
  bundle_sha256: string | null;
  /** Pin artefaktu (ZIP z repa appky); `null` u verzí z holého HTML. */
  artifact_sha256?: string | null;
  created_at: string;
  published_at: string | null;
  /** Kdy vydavatel verzi poslal k review (jak dlouho čeká). */
  submitted_at: string | null;
  reviewed_at: string | null;
  /** Co se u verze posledně vytklo — kontext pro opakované review. */
  review_note: string | null;
  /** KAN-1714 — obchody se živou instalací právě na téhle verzi. */
  installs?: PlatformAppVersionInstall[];
}

/** Obchod, který běží na dané verzi appky (KAN-1714). */
export interface PlatformAppVersionInstall {
  org_id: number;
  org_slug: string;
  org_name: string;
  status: string;
}

export interface PlatformApp {
  id: number;
  slug: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  icon_url: string | null;
  author_name: string;
  kind: string;
  price_monthly_cents: number | null;
  currency: string;
  is_listed: boolean;
  /** NULL = first-party (Forgmatic); číslo = appka třetí strany. */
  publisher_user_id: number | null;
  /** Stažená z katalogu: nová instalace nejde, běžící jedou dál. */
  withdrawn_at?: string | null;
  /** Naplánované smazání — po tomhle okamžiku ji purge smaže i s daty. */
  purge_after?: string | null;
  /** Kolik tenantů appku má (mimo odinstalované) — dopad smazání. */
  install_count?: number;
  versions: PlatformAppVersion[];
}

export interface PlatformAppsResponse {
  items: PlatformApp[];
  /** Verze ve stavu `submitted` v CELÉM katalogu — badge fronty. */
  awaiting_review_count: number;
}

/**
 * URL staff preview bundlu (``RequireFullPlatformAdmin``).
 *
 * Není to `apiFetch` — bundle se nečte do JS, ale montuje se jako `src`
 * sandboxovaného iframe, aby cizí kód běžel na opaque originu přesně
 * jako v produkci (viz `AppFrame`). Servíruje se jen pro
 * `submitted`/`approved`.
 */
export function appBundlePreviewUrl(slug: string, version: string): string {
  return `${API}/admin/platform/apps/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/preview.html`;
}

export interface PlatformAppCreatePayload {
  slug: string;
  name_cs: string;
  name_en?: string;
  description_cs?: string;
  description_en?: string;
  author_name?: string;
  icon_url?: string | null;
  price_monthly_cents?: number | null;
  currency?: string;
}

/**
 * Katalog appek; `state` z něj udělá frontu (např. `submitted`).
 *
 * `awaiting_review_count` chodí vždy nad celým katalogem, takže badge
 * po zapnutí filtru nezmizí.
 */
export function usePlatformApps(state?: string) {
  const query = state ? `?state=${encodeURIComponent(state)}` : "";
  return useQuery<PlatformAppsResponse>({
    queryKey: ["admin", "platform-apps", state ?? "all"],
    queryFn: () => apiFetch(`${API}/admin/platform/apps${query}`),
  });
}

/** Slovník scope klíčů — bez popisů je `storage:rw` v review nečitelné. */
export function useAppScopeCatalog() {
  return useQuery<{ items: AppScopeDetail[] }>({
    queryKey: ["admin", "platform-apps", "scopes"],
    queryFn: () => apiFetch(`${API}/admin/platform/apps/scopes`),
    staleTime: 300_000,
  });
}

export function useCreatePlatformApp() {
  const qc = useQueryClient();
  return useMutation<PlatformApp, Error, PlatformAppCreatePayload>({
    mutationFn: (payload) =>
      apiFetch(`${API}/admin/platform/apps`, { method: "POST", body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

export function usePatchPlatformApp(slug: string) {
  const qc = useQueryClient();
  return useMutation<PlatformApp, Error, Record<string, unknown>>({
    mutationFn: (payload) =>
      apiFetch(`${API}/admin/platform/apps/${encodeURIComponent(slug)}`, {
        method: "PATCH",
        body: payload,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

export function useUploadAppBundle(slug: string) {
  const qc = useQueryClient();
  return useMutation<
    PlatformAppVersion,
    Error,
    { version: string; file: File }
  >({
    mutationFn: ({ version, file }) => {
      // apiFetch FormData propouští beze změny (viz uploads.ts) — native
      // fetch tu jen ztrácel 401→refresh replay a typovanou ApiError.
      const form = new FormData();
      form.append("file", file);
      return apiFetch<PlatformAppVersion>(
        `${API}/admin/platform/apps/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/bundle`,
        { method: "POST", body: form },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

export function useCreateAppVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation<
    PlatformAppVersion,
    Error,
    { version: string; manifest: Record<string, unknown> }
  >({
    mutationFn: (payload) =>
      apiFetch(
        `${API}/admin/platform/apps/${encodeURIComponent(slug)}/versions`,
        { method: "POST", body: payload },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

export function useReviewAppVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation<
    PlatformAppVersion,
    Error,
    { version: string; decision: "approve" | "reject"; note?: string }
  >({
    mutationFn: ({ version, decision, note }) =>
      apiFetch(
        `${API}/admin/platform/apps/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/review`,
        { method: "POST", body: { decision, note: note ?? null } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

export function usePublishAppVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation<PlatformAppVersion, Error, string>({
    mutationFn: (version) =>
      apiFetch(
        `${API}/admin/platform/apps/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/publish`,
        { method: "POST", body: {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

/**
 * Stažení JEDNÉ publikované verze.
 *
 * Yanknutá verze okamžitě přestane mintovat tokeny, servírovat bundle
 * i obsazovat sloty — na rozdíl od kill-switche celé appky ale nechá
 * ostatní verze naživu.
 */
export function useYankAppVersion(slug: string) {
  const qc = useQueryClient();
  return useMutation<PlatformAppVersion, Error, string>({
    mutationFn: (version) =>
      apiFetch(
        `${API}/admin/platform/apps/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/yank`,
        { method: "POST", body: {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

// --- Nová verze z artefaktu + životní cyklus appky (KAN-1381) ---------

/** Odkud přijde artefakt verze — stejné tři volby jako u výpočetních modulů. */
export type AppVersionSource =
  | { kind: "zip"; version: string; file: File }
  | {
      kind: "release";
      version: string;
      repo_url: string;
      tag: string;
      asset_name?: string;
      /** Jen u privátního repa; jednorázový, BE ho neukládá. */
      pat?: string;
    };

const platformAppUrl = (slug: string) =>
  `${API}/admin/platform/apps/${encodeURIComponent(slug)}`;

/**
 * Nová draft verze jedním krokem — manifest se bere z artefaktu, ne
 * z formuláře. Spadne-li validace, draft na BE nevznikne.
 */
export function useCreateAppVersionFromSource(slug: string) {
  const qc = useQueryClient();
  return useMutation<PlatformAppVersion, Error, AppVersionSource>({
    mutationFn: (source) => {
      if (source.kind === "zip") {
        const form = new FormData();
        form.append("version", source.version);
        form.append("file", source.file);
        return apiFetch<PlatformAppVersion>(
          `${platformAppUrl(slug)}/versions/from-artifact`,
          { method: "POST", body: form },
        );
      }
      return apiFetch<PlatformAppVersion>(
        `${platformAppUrl(slug)}/versions/from-release`,
        {
          method: "POST",
          body: {
            version: source.version || null,
            repo_url: source.repo_url,
            tag: source.tag,
            asset_name: source.asset_name || null,
            pat: source.pat || null,
          },
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
    },
  });
}

export interface ScheduleAppDeletionResult {
  app: PlatformApp;
  /** Kolika adminům tenantů odešla výzva k záloze dat. */
  notified: number;
}

export interface HardDeleteAppResult {
  slug: string;
  installs_removed: number;
  versions_removed: number;
}

/**
 * Tři úrovně smazání (rozhodnutí vlastníka 2026-09-16) + návrat zpět.
 * Kill-switch (`is_listed`) sem nepatří — ten vypíná i běžící instalace.
 */
export function usePlatformAppLifecycle(slug: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "platform-apps"] });
  const withdraw = useMutation<PlatformApp, Error, void>({
    mutationFn: () =>
      apiFetch(`${platformAppUrl(slug)}/withdraw`, { method: "POST", body: {} }),
    onSuccess: invalidate,
  });
  const restore = useMutation<PlatformApp, Error, void>({
    mutationFn: () =>
      apiFetch(`${platformAppUrl(slug)}/restore`, { method: "POST", body: {} }),
    onSuccess: invalidate,
  });
  const scheduleDeletion = useMutation<ScheduleAppDeletionResult, Error, void>({
    mutationFn: () =>
      apiFetch(`${platformAppUrl(slug)}/schedule-deletion`, {
        method: "POST",
        body: {},
      }),
    onSuccess: invalidate,
  });
  const hardDelete = useMutation<HardDeleteAppResult, Error, string>({
    // Potvrzení je slug, který admin opsal — BE odmítne cokoli jiného.
    mutationFn: (confirm) =>
      apiFetch(
        `${platformAppUrl(slug)}?confirm=${encodeURIComponent(confirm)}`,
        { method: "DELETE" },
      ),
    onSuccess: invalidate,
  });
  return { withdraw, restore, scheduleDeletion, hardDelete };
}

// --- Vydavatelská plocha (/developer/apps, běžná session) -------------

export interface DeveloperVersion {
  version: string;
  state: string;
  bundle_sha256: string | null;
  review_note: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
}

export interface DeveloperApp {
  slug: string;
  name_i18n: Record<string, string>;
  is_listed: boolean;
  price_monthly_cents: number | null;
  versions: DeveloperVersion[];
}

const DEV = `${API}/developer/apps`;

export function useDeveloperApps() {
  return useQuery<{ items: DeveloperApp[] }>({
    queryKey: ["developer", "apps"],
    queryFn: () => apiFetch(DEV),
  });
}

function useDeveloperMutation<TData, TVars>(
  fn: (vars: TVars) => Promise<TData>,
) {
  const qc = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["developer", "apps"] });
    },
  });
}

export function useCreateDeveloperApp() {
  return useDeveloperMutation<DeveloperApp, { slug: string; name_cs: string }>(
    (payload) => apiFetch(DEV, { method: "POST", body: payload }),
  );
}

export function useCreateDeveloperVersion(slug: string) {
  return useDeveloperMutation<
    DeveloperVersion,
    { version: string; manifest: Record<string, unknown> }
  >((payload) =>
    apiFetch(`${DEV}/${encodeURIComponent(slug)}/versions`, {
      method: "POST",
      body: payload,
    }),
  );
}

export function useUploadDeveloperBundle(slug: string) {
  return useDeveloperMutation<DeveloperVersion, { version: string; file: File }>(
    ({ version, file }) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch(
        `${DEV}/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/bundle`,
        { method: "POST", body: form },
      );
    },
  );
}

export function useSubmitDeveloperVersion(slug: string) {
  return useDeveloperMutation<DeveloperVersion, string>((version) =>
    apiFetch(
      `${DEV}/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version)}/submit`,
      { method: "POST", body: {} },
    ),
  );
}
